import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { formatPrice } from "@/lib/api";
import QuickView from "@/components/QuickView";
import { useLanguage } from "@/context/LanguageContext";

export default function ProductCard({ product, index = 0 }) {
    const { t, lang } = useLanguage();
    const navigate = useNavigate();
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const name = lang === "en" && product.name_en ? product.name_en : product.name;

    return (
        <>
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: (index % 3) * 0.1, duration: 0.6 }}
                className="group cursor-pointer"
                onClick={() => navigate(`/prodotto/${product.id}`)}
                data-testid={`product-card-${product.id}`}
            >
                <div className="relative overflow-hidden bg-zinc-100 aspect-[3/4]">
                    <img
                        src={product.images?.length ? product.images[0] : product.image}
                        alt={name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    {product.sold_out && (
                        <span
                            data-testid={`sold-out-badge-${product.id}`}
                            className="absolute top-4 left-4 bg-black text-white text-[10px] tracking-[0.3em] uppercase font-bold px-4 py-2"
                        >
                            {t("product.soldOut")}
                        </span>
                    )}
                    {!product.sold_out && (
                        <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full transition-transform duration-500 ease-out group-hover:translate-y-0">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setQuickViewOpen(true);
                                }}
                                data-testid={`quick-view-open-${product.id}`}
                                className="block w-full bg-white/90 backdrop-blur text-center py-3 text-[10px] tracking-[0.3em] uppercase font-semibold hover:bg-white transition-colors"
                            >
                                {t("product.quickView")}
                            </button>
                        </div>
                    )}
                </div>
                <div className="pt-4 flex items-start justify-between">
                    <div>
                        <h3 className="font-semibold text-sm md:text-base">{name}</h3>
                        <p className="text-xs text-zinc-500 uppercase tracking-widest mt-1">
                            {t(`product.categories.${product.category}`)}
                        </p>
                    </div>
                    <p className="font-display font-bold text-sm md:text-base" data-testid={`product-price-${product.id}`}>
                        {formatPrice(product.price)}
                    </p>
                </div>
            </motion.div>
            <QuickView product={product} open={quickViewOpen} onOpenChange={setQuickViewOpen} />
        </>
    );
}
